import { forEach, mapKeys, pickBy, snakeCase, startCase } from 'lodash/fp';
import { getHostedFieldSelector } from '@zapier/payment/src/components/CreditCardForm/HostedFields/utils';
import {
  CreditCardBrandCode,
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from '@zapier/payment/src/types';

declare global {
  interface Window {
    Stripe?: (apiKey: string) => void;
  }
}

function loadScript(url: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;

    if (document.body) {
      document.body.appendChild(script);
    }
  });
}

/** @internal */
export class StripeProcessor implements HostedFieldProcessorInterface {
  stripe: any;
  elements: any;
  static instance: StripeProcessor | undefined | null = null;
  static elementsRegistry: Map<HostedFieldType, any> = new Map();

  constructor(stripe: any) {
    this.stripe = stripe;
    this.elements = stripe.elements({
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
        },
      ],
    });
  }

  async createFields(
    style: any,
    onChange: (b: HostedFieldType, a: HostedFieldValue) => void
  ) {
    const types = ['number', 'expiry', 'cvc'] as const;

    forEach<HostedFieldType>(type => {
      const element = this.elements.create(`card${startCase(type)}`, {
        style: {
          base: style,
        },
      });

      StripeProcessor.elementsRegistry.set(type, element);

      element.addEventListener(
        'change',
        (
          event: GlobalEventHandlersEventMap['change'] & {
            empty: boolean;
            error: { message: string };
            brand: CreditCardBrandCode;
          }
        ) => {
          onChange(type, {
            isEmpty: event.empty,
            originalError: event.error ? event.error.message : '',
            brand: event.brand || 'unknown',
          });
        }
      );

      element.mount(getHostedFieldSelector(type));
    }, types);
  }

  destroyFields() {
    const types = ['number', 'expiry', 'cvc'] as const;

    forEach(type => {
      const element = StripeProcessor.elementsRegistry.get(type);

      if (element) {
        element.destroy();
        StripeProcessor.elementsRegistry.delete(type);
      }
    }, types);
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const firstElement = StripeProcessor.elementsRegistry.values().next().value;
    const cleanData = pickBy(Boolean, additionalData);
    const data = mapKeys(snakeCase, cleanData);
    const { error, token } = await this.stripe.createToken(firstElement, data);

    if (error) {
      throw new Error(error.message);
    }

    return {
      token: token.id,
      details: token.card,
    };
  }

  static async create(apiKey: string): Promise<HostedFieldProcessorInterface> {
    if (!StripeProcessor.instance) {
      if (!window.Stripe) {
        await loadScript('https://js.stripe.com/v3/');
      }

      StripeProcessor.instance = new StripeProcessor(window.Stripe?.(apiKey));
    }

    return StripeProcessor.instance;
  }
}

export default StripeProcessor.create;
